import { Field } from '@/components/ui/field';
import {
  useRemoveOutreachEmailAction,
  useUpdateOutreachEmailAction,
} from '@/hooks/actions/use-update-outreach-email-action';
import { isValidEmail } from '@/lib/is-valid-email';
import { Button, Flex, Input } from '@chakra-ui/react';
import type { Brand } from '@fluenceur/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SettingsSection from './SettingsSection';

interface OutreachEmailSectionProps {
  brand: Brand;
  isLast?: boolean;
}

const OutreachEmailSection = ({ brand, isLast }: OutreachEmailSectionProps) => {
  const { t } = useTranslation();
  const brandOutreachEmail = brand.settings?.outreachEmail || '';
  const [email, setEmail] = useState<string>(brandOutreachEmail);
  const [originalEmail, setOriginalEmail] = useState<string>(brandOutreachEmail);
  const [error, setError] = useState<string>('');

  const updateOutreachEmail = useUpdateOutreachEmailAction(brand.id);
  const removeOutreachEmail = useRemoveOutreachEmailAction(brand.id);

  useEffect(() => {
    const nextEmail = brand.settings?.outreachEmail || '';
    setEmail(nextEmail);
    setOriginalEmail(nextEmail);
  }, [brand.settings?.outreachEmail]);

  const savedEmail =
    updateOutreachEmail.data?.data?.outreachEmail ?? updateOutreachEmail.variables?.email;

  useEffect(() => {
    if (!updateOutreachEmail.isSuccess || savedEmail === undefined) {
      return;
    }
    setOriginalEmail(savedEmail || '');
    setEmail(savedEmail || '');
  }, [savedEmail, updateOutreachEmail.isSuccess]);

  useEffect(() => {
    if (!removeOutreachEmail.isSuccess) {
      return;
    }
    setOriginalEmail('');
    setEmail('');
  }, [removeOutreachEmail.isSuccess]);

  const validateEmail = (value: string): boolean => {
    if (!value) {
      setError(t('settings.outreachEmail.errorRequired'));
      return false;
    }
    if (!isValidEmail(value)) {
      setError(t('settings.outreachEmail.errorInvalidEmail'));
      return false;
    }
    setError('');
    return true;
  };

  const handleSave = () => {
    if (!validateEmail(email)) {
      return;
    }
    updateOutreachEmail.mutate({ email });
  };

  const handleRemove = () => {
    removeOutreachEmail.mutate();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      setError('');
    }
  };

  const hasChanges = email !== originalEmail;
  const isPending = updateOutreachEmail.isPending || removeOutreachEmail.isPending;
  const hasExistingEmail = !!originalEmail;

  return (
    <SettingsSection
      title={t('settings.outreachEmail.title')}
      description={t('settings.outreachEmail.description')}
      isLast={isLast}
    >
      <Field invalid={!!error} errorText={error} width="full" maxWidth="400px">
        <Input
          variant="outline"
          type="email"
          placeholder={t('settings.outreachEmail.placeholder')}
          value={email}
          onChange={handleEmailChange}
          disabled={isPending}
        />
      </Field>

      <Flex gap={2} mt={4}>
        <Button
          size="sm"
          disabled={!hasChanges || isPending || !email}
          onClick={handleSave}
          loading={updateOutreachEmail.isPending}
        >
          {t('settings.outreachEmail.save')}
        </Button>
        {hasExistingEmail && (
          <Button
            size="sm"
            variant="outline"
            colorPalette="red"
            onClick={handleRemove}
            disabled={isPending}
            loading={removeOutreachEmail.isPending}
          >
            {t('settings.outreachEmail.remove')}
          </Button>
        )}
        {hasChanges && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setEmail(originalEmail);
              setError('');
            }}
            disabled={isPending}
          >
            {t('common.cancel')}
          </Button>
        )}
      </Flex>
    </SettingsSection>
  );
};

export default OutreachEmailSection;
